import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield, ShieldAlert, ShieldCheck, Lock, Unlock, Eye,
  Smartphone, Key, RefreshCw, AlertTriangle, CheckCircle,
  Users, Activity, XCircle, ChevronDown, ChevronRight, FileText, Download, Mail, Send
} from 'lucide-react';

const API  = process.env.REACT_APP_BACKEND_URL + '/api/wms/security';
const DOCS = process.env.REACT_APP_BACKEND_URL + '/api/wms/docs';

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('wms_token') || ''}` }
});

const THREAT_COLOR = {
  low:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  high:   'bg-red-100 text-red-700 border-red-200',
};
const THREAT_ICON = { low: ShieldCheck, medium: Shield, high: ShieldAlert };

export default function SecurityTab({ user }) {
  const [dashboard, setDashboard]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showLogs, setShowLogs]     = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);

  // 2FA setup flow
  const [tfaView, setTfaView]       = useState('idle'); // idle|setup|confirm|done
  const [tfaData, setTfaData]       = useState(null);   // {secret, qr_code_base64}
  const [tfaCode, setTfaCode]       = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);
  const [tfaError, setTfaError]     = useState('');

  // 2FA email provisioning (admin)
  const [emailTarget, setEmailTarget]   = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult]   = useState(null); // {success, message, qr_code_base64?}
  const [showQrInline, setShowQrInline] = useState(false);

  // Chain verify
  const [chainResult, setChainResult] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);

  const [unlocking, setUnlocking] = useState({});
  const [actionMsg, setActionMsg] = useState('');

  // Disable-2FA modal state
  const [disableOpen, setDisableOpen]       = useState(false);
  const [disablePwd, setDisablePwd]         = useState('');
  const [disableError, setDisableError]     = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  const send2FASetupEmail = async () => {
    setEmailSending(true); setEmailResult(null); setShowQrInline(false);
    try {
      const r = await axios.post(`${API}/2fa/send-setup-email`, { email: emailTarget || user?.email }, authHeader());
      setEmailResult(r.data);
      if (r.data.qr_code_base64) setShowQrInline(true);
    } catch (e) {
      setEmailResult({ success: false, message: e.response?.data?.detail || 'Failed to send email' });
    }
    setEmailSending(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard`, authHeader());
      setDashboard(r.data);
    } catch (e) {
      console.error('Security dashboard load failed:', e.response?.status, e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startSetup2FA = async () => {
    setTfaView('setup'); setTfaError('');
    setTfaLoading(true);
    try {
      const r = await axios.post(`${API}/2fa/setup`, {}, authHeader());
      setTfaData(r.data);
      setTfaView('scan');
    } catch (e) {
      setTfaError(e.response?.data?.detail || 'Setup failed');
      setTfaView('idle');
    }
    setTfaLoading(false);
  };

  const confirm2FA = async () => {
    setTfaLoading(true); setTfaError('');
    try {
      await axios.post(`${API}/2fa/confirm`, { code: tfaCode }, authHeader());
      setTfaView('done');
      setActionMsg('2FA enabled successfully. You will need your authenticator app on next login.');
      load();
    } catch (e) {
      setTfaError(e.response?.data?.detail || 'Invalid code');
    }
    setTfaLoading(false);
    setTfaCode('');
  };

  const disable2FA = async () => {
    setDisablePwd('');
    setDisableError('');
    setDisableOpen(true);
  };

  const confirmDisable2FA = async () => {
    if (!disablePwd) {
      setDisableError('Password is required');
      return;
    }
    setDisableLoading(true);
    setDisableError('');
    try {
      await axios.post(`${API}/2fa/disable`, { password: disablePwd }, authHeader());
      setDisableOpen(false);
      setDisablePwd('');
      setActionMsg('2FA disabled.');
      setTfaView('idle');
      load();
    } catch (e) {
      setDisableError(e.response?.data?.detail || 'Failed to disable 2FA');
    }
    setDisableLoading(false);
  };

  const unlockAccount = async (email) => {
    setUnlocking(p => ({ ...p, [email]: true }));
    try {
      await axios.post(`${API}/unlock/${email}`, {}, authHeader());
      setActionMsg(`Account ${email} unlocked.`);
      load();
    } catch (e) {
      setActionMsg(e.response?.data?.detail || 'Failed');
    }
    setUnlocking(p => ({ ...p, [email]: false }));
  };

  const verifyChain = async () => {
    setChainLoading(true);
    try {
      const r = await axios.get(`${API}/audit/verify-chain?last_n=50`, authHeader());
      setChainResult(r.data);
    } catch (e) {
      setChainResult({ chain_valid: false, message: 'Verification failed' });
    }
    setChainLoading(false);
  };

  const resetChain = async () => {
    if (!window.confirm('Reset the audit hash chain? This clears broken chain metadata but keeps all audit log entries.')) return;
    try {
      await axios.post(`${API}/audit/reset-chain`, {}, authHeader());
      setChainResult({ chain_valid: true, message: 'Chain reset. New audit entries will be chained correctly going forward.' });
      load();
    } catch (e) {
      setChainResult({ chain_valid: false, message: e.response?.data?.detail || 'Reset failed' });
    }
  };

  const revokeAllSessions = async () => {
    if (!window.confirm('Sign out all devices? You will need to log in again.')) return;
    try {
      const r = await axios.post(`${API}/sessions/revoke-all`, {}, authHeader());
      setActionMsg(`${r.data.revoked} sessions revoked. Log in again on other devices.`);
    } catch { setActionMsg('Failed'); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400"><RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" /><p>Loading security data...</p></div>;

  const d = dashboard;
  const ThreatIcon = THREAT_ICON[d?.threat_level] || Shield;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Action message */}
      {actionMsg && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start justify-between">
          <p className="text-sm text-blue-700">{actionMsg}</p>
          <button onClick={() => setActionMsg('')} className="text-blue-400 hover:text-blue-600 ml-3"><XCircle className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── THREAT LEVEL ──────────────────────────────────────────────── */}
      <div className={`rounded-xl border-2 p-5 ${THREAT_COLOR[d?.threat_level] || 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ThreatIcon className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Security Status: {(d?.threat_level || 'low').toUpperCase()}</h2>
              <p className="text-sm mt-0.5">
                {d?.failed_logins_24h} failed logins in last 24h · {d?.locked_accounts?.length || 0} accounts locked · {d?.security_alerts_7d} alerts (7d)
              </p>
            </div>
          </div>
          <button onClick={load} className="p-2 rounded-lg hover:opacity-70"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* ── STATS GRID ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Failed Logins (24h)', value: d?.failed_logins_24h ?? 0, icon: ShieldAlert, bad: d?.failed_logins_24h > 5, color: 'red' },
          { label: 'Successful Logins', value: d?.success_logins_24h ?? 0, icon: ShieldCheck, color: 'emerald' },
          { label: '2FA Enabled Users', value: `${d?.tfa_enabled_users ?? 0} / ${d?.total_users ?? 0}`, icon: Smartphone, color: 'blue' },
          { label: 'Audit Chain', value: d?.audit_chain_ok ? 'Intact ✓' : 'BROKEN ⚠', icon: Key, bad: !d?.audit_chain_ok, color: d?.audit_chain_ok ? 'emerald' : 'red' },
        ].map(({ label, value, icon: Icon, color, bad }) => (
          <div key={label} className={`bg-white rounded-xl p-4 border shadow-sm ${bad ? 'border-red-200' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">{label}</span>
              <div className={`w-7 h-7 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                <Icon className={`w-3.5 h-3.5 text-${color}-500`} />
              </div>
            </div>
            <p className={`text-xl font-bold ${bad ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* ── 2FA MANAGEMENT ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-blue-600" />Two-Factor Authentication (2FA)</h3>
          <p className="text-xs text-gray-500 mt-0.5">Requires a 6-digit code from Google Authenticator on every login</p>
        </div>
        <div className="p-5">
          {tfaView === 'idle' && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">2FA Status: <span className={d?.tfa_enabled_users > 0 ? 'text-emerald-600' : 'text-amber-600'}>{d?.tfa_enabled_users > 0 ? 'Enabled for your account' : 'Not enabled'}</span></p>
                <p className="text-xs text-gray-500 mt-0.5">Enable 2FA to protect your account against stolen passwords.</p>
              </div>
              {d?.tfa_enabled_users > 0
                ? <button onClick={disable2FA} className="flex items-center gap-2 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm hover:bg-red-50"><Lock className="w-4 h-4" />Disable 2FA</button>
                : <button onClick={startSetup2FA} disabled={tfaLoading} className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 disabled:bg-gray-300 shadow-sm"><Smartphone className="w-4 h-4" />{tfaLoading ? 'Setting up...' : 'Enable 2FA'}</button>
              }
            </div>
          )}

          {tfaView === 'scan' && tfaData && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-blue-800">Step 1 — Scan this QR code with Google Authenticator</p>
              <div className="flex gap-6 items-start">
                <img src={`data:image/png;base64,${tfaData.qr_code_base64}`} alt="QR Code" className="w-40 h-40 border rounded-xl" />
                <div className="flex-1">
                  <p className="text-xs text-gray-600 mb-2">Or enter this key manually in the app:</p>
                  <code className="text-xs bg-gray-100 px-3 py-2 rounded-lg block font-mono break-all">{tfaData.secret}</code>
                  <p className="text-xs text-gray-500 mt-3">App: <strong>Google Authenticator</strong> or <strong>Authy</strong></p>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-800 mb-2">Step 2 — Enter the 6-digit code shown in the app</p>
                <div className="flex gap-3">
                  <input type="text" maxLength={6} value={tfaCode} onChange={e => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456" className="w-32 border rounded-lg px-3 py-2 text-lg text-center font-mono tracking-widest focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                  <button onClick={confirm2FA} disabled={tfaLoading || tfaCode.length !== 6}
                    className="bg-blue-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 disabled:bg-gray-300">
                    {tfaLoading ? 'Verifying...' : 'Activate 2FA'}
                  </button>
                  <button onClick={() => { setTfaView('idle'); setTfaData(null); }} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                </div>
                {tfaError && <p className="text-xs text-red-600 mt-2">{tfaError}</p>}
              </div>
            </div>
          )}

          {tfaView === 'done' && (
            <div className="flex items-center gap-3 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              <p className="text-sm font-medium">2FA enabled successfully! You will need your authenticator app on next login.</p>
            </div>
          )}

          {/* Admin: send QR setup email to any user */}
          {user?.role === 'admin' && (
            <div className="mt-5 pt-5 border-t border-dashed">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />Admin — Send 2FA Setup Email
              </p>
              <p className="text-xs text-gray-400 mb-3">Email a QR code to any user so they can set up 2FA without doing it themselves.</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={emailTarget}
                  onChange={e => setEmailTarget(e.target.value)}
                  placeholder={user?.email || 'user@example.com'}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
                  data-testid="2fa-email-target-input"
                />
                <button
                  onClick={send2FASetupEmail}
                  disabled={emailSending}
                  data-testid="send-2fa-email-btn"
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 shadow-sm shrink-0">
                  <Send className="w-4 h-4" />
                  {emailSending ? 'Sending...' : 'Send QR Email'}
                </button>
              </div>

              {emailResult && (
                <div className={`mt-3 rounded-xl p-3 border text-sm ${emailResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                  <div className="flex items-start gap-2">
                    {emailResult.success
                      ? <CheckCircle className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                      : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />}
                    <div className="flex-1">
                      <p>{emailResult.message}</p>
                      {emailResult.qr_code_base64 && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-700 mb-2">Gmail not configured — share this QR code manually:</p>
                          <button onClick={() => setShowQrInline(v => !v)} className="text-xs underline text-blue-600">
                            {showQrInline ? 'Hide QR Code' : 'Show QR Code'}
                          </button>
                          {showQrInline && (
                            <div className="mt-2 p-3 bg-white rounded-lg border inline-block">
                              <img src={`data:image/png;base64,${emailResult.qr_code_base64}`} alt="2FA QR" className="w-40 h-40" />
                              <p className="text-[10px] text-gray-400 text-center mt-1">Right-click → Save Image</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setEmailResult(null)} className="text-gray-400 hover:text-gray-600"><XCircle className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── LOCKED ACCOUNTS ───────────────────────────────────────────── */}
      {d?.locked_accounts?.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b bg-red-50">
            <h3 className="font-semibold flex items-center gap-2 text-red-800"><Lock className="w-4 h-4" />Locked Accounts ({d.locked_accounts.length})</h3>
            <p className="text-xs text-red-600 mt-0.5">These accounts are locked due to too many failed login attempts</p>
          </div>
          <div className="divide-y">
            {d.locked_accounts.map(email => (
              <div key={email} className="flex items-center justify-between px-5 py-3">
                <p className="text-sm font-medium">{email}</p>
                <button onClick={() => unlockAccount(email)} disabled={unlocking[email]}
                  className="flex items-center gap-1.5 text-xs bg-emerald-500 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-600 disabled:bg-gray-300">
                  <Unlock className="w-3.5 h-3.5" />{unlocking[email] ? 'Unlocking...' : 'Unlock'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AUDIT CHAIN INTEGRITY ──────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-slate-50">
          <h3 className="font-semibold flex items-center gap-2"><Key className="w-4 h-4 text-gray-600" />Audit Trail Integrity</h3>
          <p className="text-xs text-gray-500 mt-0.5">SHA-256 hash chain — detects if any audit log entry was modified</p>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-4">
            <div className={`flex-1 rounded-xl p-4 border ${chainResult ? (chainResult.chain_valid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200') : 'bg-gray-50 border-gray-200'}`}>
              {chainResult ? (
                <div className="flex items-start gap-3">
                  {chainResult.chain_valid
                    ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                  <div>
                    <p className={`text-sm font-semibold ${chainResult.chain_valid ? 'text-emerald-800' : 'text-red-800'}`}>
                      {chainResult.chain_valid ? 'Chain Intact' : 'Chain Broken!'}
                    </p>
                    <p className={`text-xs mt-0.5 ${chainResult.chain_valid ? 'text-emerald-600' : 'text-red-600'}`}>{chainResult.message}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{chainResult.entries_checked} entries verified</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Click "Verify" to check audit log integrity</p>
              )}
            </div>
            <button onClick={verifyChain} disabled={chainLoading}
              className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 disabled:bg-gray-400 shrink-0">
              <Key className={`w-4 h-4 ${chainLoading ? 'animate-pulse' : ''}`} />
              {chainLoading ? 'Verifying...' : 'Verify Chain'}
            </button>
            {chainResult && !chainResult.chain_valid && (
              <button onClick={resetChain}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 shrink-0">
                <Key className="w-4 h-4" />Reset Chain
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── SESSION MANAGEMENT ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b">
          <h3 className="font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-gray-600" />Session Management</h3>
        </div>
        <div className="p-5 flex items-center justify-between">
          <div>
            <p className="text-sm">Tokens expire after <strong>8 hours</strong>. Click "Sign Out Everywhere" to invalidate all active sessions.</p>
            <p className="text-xs text-gray-500 mt-1">Use this if you suspect your account has been compromised.</p>
          </div>
          <button onClick={revokeAllSessions} className="flex items-center gap-2 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm hover:bg-red-50 shrink-0">
            <XCircle className="w-4 h-4" />Sign Out Everywhere
          </button>
        </div>
      </div>

      {/* ── RECENT FAILED LOGINS ──────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button onClick={() => setShowLogs(v => !v)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50">
          <h3 className="font-semibold flex items-center gap-2">
            <Eye className="w-4 h-4 text-gray-500" />Recent Failed Logins
            {d?.failed_logins_24h > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{d.failed_logins_24h} in 24h</span>}
          </h3>
          {showLogs ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {showLogs && (
          <div className="border-t">
            {d?.recent_failed_logins?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  {['Email', 'IP Address', 'Time'].map(h => <th key={h} className="px-4 py-2 text-left text-xs text-gray-500 font-medium">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y">
                  {d.recent_failed_logins.map((f, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">{f.email}</td>
                      <td className="px-4 py-2 text-xs font-mono text-gray-500">{f.ip}</td>
                      <td className="px-4 py-2 text-xs text-gray-400">{f.timestamp?.replace('T', ' ').slice(0, 19)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-gray-400 text-sm">No failed logins recorded</div>
            )}
          </div>
        )}
      </div>

      {/* ── COMPLIANCE REPORT ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-blue-50">
          <h3 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" />Compliance Report</h3>
          <p className="text-xs text-gray-500 mt-0.5">Auto-generated security & compliance report</p>
        </div>
        <div className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-medium">Security Report</p>
            <p className="text-xs text-gray-500 mt-1">Covers all 10 security controls, data privacy, API security, audit trail, and tech stack. Includes a declaration section for signing.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.open(`${DOCS}/compliance-report/html`, '_blank')}
              className="flex items-center gap-2 border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              <Eye className="w-4 h-4" />Preview HTML
            </button>
            <button onClick={() => window.open(`${DOCS}/compliance-report/pdf`, '_blank')}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 shadow-sm">
              <Download className="w-4 h-4" />Download PDF
            </button>
          </div>
        </div>
        <div className="px-5 pb-4">
          <div className="bg-indigo-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-indigo-800">
            {['Executive Summary', 'Live System Stats', '10 Security Controls', 'Shopee Requirements', 'Data Privacy (PDPA)', 'API Security Architecture', 'Audit Trail Design', 'Technology Stack', 'Declaration + Signature'].map(s => (
              <div key={s} className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-indigo-500 shrink-0" />{s}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECURITY CHECKLIST ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" />Security Checklist</h3>
        <div className="space-y-2">
          {[
            { label: 'bcrypt password hashing (12 rounds)',       done: true },
            { label: 'Brute-force lockout (5 attempts → 30 min)', done: true },
            { label: 'JWT tokens expire after 8 hours',           done: true },
            { label: 'Input sanitisation (NoSQL injection guard)', done: true },
            { label: 'Immutable audit log with hash chain',        done: true },
            { label: 'Role-based access control (Admin/Manager)',  done: true },
            { label: 'Suspicious activity email alerts',          done: true },
            { label: 'Two-Factor Authentication (2FA)',            done: d?.tfa_enabled_users > 0 },
            { label: 'Gmail alerts configured',                   done: !!process.env.REACT_APP_BACKEND_URL },
            { label: 'Export restricted to Admin role',           done: true },
          ].map(({ label, done }) => (
            <div key={label} className="flex items-center gap-3">
              {done ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />}
              <span className={`text-sm ${done ? 'text-gray-700' : 'text-amber-700'}`}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Disable 2FA confirmation modal ─────────────────────────────── */}
      {disableOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => !disableLoading && setDisableOpen(false)}
          data-testid="disable-2fa-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-4 p-6 pb-4 border-b border-gray-100">
              <div className="shrink-0 w-11 h-11 rounded-full bg-red-50 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Disable Two-Factor Authentication?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Your account will rely on password only. You can re-enable 2FA later.
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm with your current password
              </label>
              <input
                type="password"
                value={disablePwd}
                onChange={(e) => { setDisablePwd(e.target.value); setDisableError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && confirmDisable2FA()}
                placeholder="••••••••"
                autoFocus
                disabled={disableLoading}
                className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition disabled:bg-gray-50"
                data-testid="disable-2fa-password-input"
              />
              {disableError && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1.5" data-testid="disable-2fa-error">
                  <XCircle className="w-3.5 h-3.5" />
                  {disableError}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setDisableOpen(false)}
                disabled={disableLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition disabled:opacity-50"
                data-testid="disable-2fa-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmDisable2FA}
                disabled={disableLoading || !disablePwd}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="disable-2fa-confirm-btn"
              >
                {disableLoading ? (
                  <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Disabling…</>
                ) : (
                  <><Lock className="w-3.5 h-3.5" /> Disable 2FA</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
